import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, Transition as _Transition, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, resolveDynamicComponent as _resolveDynamicComponent, normalizeClass as _normalizeClass, createBlock as _createBlock, toDisplayString as _toDisplayString, renderSlot as _renderSlot, createTextVNode as _createTextVNode, unref as _unref, Teleport as _Teleport } from "vue"

const _hoisted_1 = ["aria-labelledby"]
const _hoisted_2 = { class: "flex items-end justify-center min-h-screen pt-0 px-0 pb-0 text-center sm:block sm:p-0 max-[730px]:!block max-[730px]:!p-0" }
const _hoisted_3 = { class: "flex items-center justify-between" }
const _hoisted_4 = { class: "flex items-center" }
const _hoisted_5 = ["id"]
const _hoisted_6 = ["aria-label"]
const _hoisted_7 = {
  key: 1,
  class: "bg-gray-50 dark:bg-gray-700 px-4 py-4 sm:px-6 border-t border-gray-200 dark:border-gray-600"
}
const _hoisted_8 = { class: "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-3 space-y-3 space-y-reverse sm:space-y-0" }

import { computed } from 'vue'
import { X } from 'lucide-vue-next'

export interface Props {
  isVisible: boolean
  title?: string
  subtitle?: string
  icon?: any
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | 'full'
  type?: 'default' | 'danger' | 'success' | 'warning' | 'info' | 'secondary' | 'forest' | 'primary'
  modalType?: 'modal' | 'offcanvas'
  showHeader?: boolean
  showFooter?: boolean
  showCloseButton?: boolean
  closableByBackdrop?: boolean
  scrollable?: boolean
  contentPadding?: string
  maxHeight?: string
  ariaLabelledby?: string
  closeButtonLabel?: string
}


export default /*@__PURE__*/_defineComponent({
  __name: 'BaseModal',
  props: {
    isVisible: { type: Boolean },
    title: { default: '' },
    subtitle: { default: '' },
    icon: { default: undefined },
    size: { default: 'lg' },
    type: { default: 'default' },
    modalType: { default: 'modal' },
    showHeader: { type: Boolean, default: true },
    showFooter: { type: Boolean, default: false },
    showCloseButton: { type: Boolean, default: true },
    closableByBackdrop: { type: Boolean, default: true },
    scrollable: { type: Boolean, default: true },
    contentPadding: { default: 'px-4 py-4 sm:px-6 sm:py-6' },
    maxHeight: { default: 'max-h-[90vh]' },
    ariaLabelledby: { default: 'modal-title' },
    closeButtonLabel: { default: 'Close modal' }
  },
  emits: ["close"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

// Computed properties for dynamic styling
const modalSize = computed(() => {
  const sizes = {
    sm: 'max-w-full sm:max-w-sm',
    md: 'max-w-full sm:max-w-md', 
    lg: 'max-w-full sm:max-w-lg',
    xl: 'max-w-full sm:max-w-xl',
    '2xl': 'max-w-full max-[730px]:!max-w-full max-[730px]:!w-full max-[730px]:!h-full max-[730px]:!m-0 max-[730px]:!rounded-none sm:max-w-2xl',
    '3xl': 'max-w-full sm:max-w-3xl',
    '4xl': 'max-w-full sm:max-w-4xl',
    full: 'max-w-full'
  }
  return sizes[props.size]
})

const headerGradient = computed(() => {
  const gradients = {
    default: 'bg-[#0b6623]',
    forest: 'bg-[#0b6623]',
    secondary: 'bg-[#0275b1]',
    danger: 'bg-danger',
    success: 'bg-success',
    warning: 'bg-warning',
    info: 'bg-info',
    primary: 'bg-primary'
  }
  return gradients[props.type] || gradients.default
})

const isLightHeader = computed(() => {
  return props.type === 'primary' || props.type === 'warning'
})

const headerTheme = computed(() => {
  if (isLightHeader.value) {
    return {
      title: 'text-gray-900 font-bold tracking-tight',
      subtitle: 'text-gray-800/90 font-medium',
      icon: 'text-gray-900',
      iconBg: 'bg-black/10',
      closeBtn: 'text-gray-900 hover:bg-black/10',
    }
  }

  return {
    title: 'text-white font-semibold',
    subtitle: 'text-white/80',
    icon: 'text-white',
    iconBg: 'bg-white/20 backdrop-blur-sm',
    closeBtn: 'text-white hover:bg-white/20',
  }
})

// Handle backdrop click
const handleBackdropClick = () => {
  if (props.closableByBackdrop) {
    emit('close')
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
    _createVNode(_Transition, { name: "modal-fade" }, {
      default: _withCtx(() => [
        (_ctx.isVisible)
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              class: "fixed inset-0 z-50 overflow-y-auto max-[730px]:!overflow-hidden",
              "aria-labelledby": _ctx.ariaLabelledby,
              role: "dialog",
              "aria-modal": "true"
            }, [
              _createElementVNode("div", _hoisted_2, [
                _createVNode(_Transition, { name: "overlay-fade" }, {
                  default: _withCtx(() => [
                    (_ctx.isVisible)
                      ? (_openBlock(), _createElementBlock("div", {
                          key: 0,
                          class: "fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm transition-all duration-300",
                          "aria-hidden": "true",
                          onClick: handleBackdropClick
                        }))
                      : _createCommentVNode("", true)
                  ]),
                  _: 1
                }),
                _cache[1] || (_cache[1] = _createElementVNode("span", {
                  class: "hidden sm:inline-block sm:align-middle sm:h-screen max-[730px]:!hidden",
                  "aria-hidden": "true"
                }, "​", -1)),
                _createVNode(_Transition, { name: "modal-slide" }, {
                  default: _withCtx(() => [
                    (_ctx.isVisible)
                      ? (_openBlock(), _createElementBlock("div", {
                          key: 0,
                          class: _normalizeClass(["relative inline-block align-bottom bg-white dark:bg-gray-800 text-left overflow-hidden shadow-2xl transform transition-all w-full sm:my-8 sm:align-middle max-[730px]:!flex max-[730px]:!flex-col max-[730px]:!fixed max-[730px]:!inset-0 max-[730px]:!m-0 max-[730px]:!h-screen max-[730px]:!max-h-screen max-[730px]:!w-screen max-[730px]:!align-baseline", [
                modalSize.value,
                _ctx.modalType === 'offcanvas' ? 'fixed inset-y-0 right-0 h-full max-w-full pl-10' : 'sm:rounded-3xl min-h-screen sm:min-h-0 max-[730px]:!rounded-none max-[730px]:!min-h-screen'
              ]])
                        }, [
                          (_ctx.showHeader)
                            ? (_openBlock(), _createElementBlock("div", {
                                key: 0,
                                class: _normalizeClass([
                  'px-4 py-4 sm:px-6',
                  headerGradient.value,
                  _ctx.modalType === 'offcanvas' ? 'rounded-tl-3xl' : ''
                ])
                              }, [
                                _createElementVNode("div", _hoisted_3, [
                                  _createElementVNode("div", _hoisted_4, [
                                    (_ctx.icon)
                                      ? (_openBlock(), _createElementBlock("div", {
                                          key: 0,
                                          class: _normalizeClass(["flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full transition-colors", headerTheme.value.iconBg])
                                        }, [
                                          (_openBlock(), _createBlock(_resolveDynamicComponent(_ctx.icon), {
                                            class: _normalizeClass(["h-6 w-6", headerTheme.value.icon])
                                          }, null, 8, ["class"]))
                                        ], 2))
                                      : _createCommentVNode("", true),
                                    _createElementVNode("div", {
                                      class: _normalizeClass(_ctx.icon ? 'ml-4' : '')
                                    }, [
                                      _createElementVNode("h3", {
                                        class: _normalizeClass(["text-xl", headerTheme.value.title]),
                                        id: _ctx.ariaLabelledby
                                      }, _toDisplayString(_ctx.title), 11, _hoisted_5),
                                      (_ctx.subtitle)
                                        ? (_openBlock(), _createElementBlock("p", {
                                            key: 0,
                                            class: _normalizeClass(["text-sm mt-1", headerTheme.value.subtitle])
                                          }, [
                                            _renderSlot(_ctx.$slots, "subtitle", {}, () => [
                                              _createTextVNode(_toDisplayString(_ctx.subtitle), 1)
                                            ])
                                          ], 2))
                                        : _createCommentVNode("", true)
                                    ], 2)
                                  ]),
                                  (_ctx.showCloseButton)
                                    ? (_openBlock(), _createElementBlock("button", {
                                        key: 0,
                                        onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('close'))),
                                        class: _normalizeClass(["p-2 rounded-lg transition-colors", headerTheme.value.closeBtn]),
                                        "aria-label": _ctx.closeButtonLabel
                                      }, [
                                        _createVNode(_unref(X), { class: "w-5 h-5" })
                                      ], 10, _hoisted_6))
                                    : _createCommentVNode("", true)
                                ])
                              ], 2))
                            : _createCommentVNode("", true),
                          _createElementVNode("div", {
                            class: _normalizeClass([
                  _ctx.contentPadding,
                  _ctx.scrollable ? 'overflow-y-auto' : '',
                  _ctx.modalType === 'offcanvas' && _ctx.showHeader ? 'flex-1' : '',
                  _ctx.maxHeight,
                  'max-[730px]:!flex-1 max-[730px]:!overflow-y-auto max-[730px]:!max-h-none'
                ])
                          }, [
                            _renderSlot(_ctx.$slots, "default")
                          ], 2),
                          (_ctx.showFooter)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                                _createElementVNode("div", _hoisted_8, [
                                  _renderSlot(_ctx.$slots, "footer")
                                ])
                              ]))
                            : _createCommentVNode("", true)
                        ], 2))
                      : _createCommentVNode("", true)
                  ]),
                  _: 3
                })
              ])
            ], 8, _hoisted_1))
          : _createCommentVNode("", true)
      ]),
      _: 3
    })
  ]))
}
}

})